import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Image, Upload, Loader2, Video, X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Visual references (Bildreferenzen) — extracted verbatim from Settings.js so
// BOTH the Settings page and the new BrandDnaView use the SAME component.
// Self-contained: owns its state, loads its own data, and writes to the
// GENERATION-EFFECTIVE target `brand_media` (POST /brand/upload-media) — the
// collection fetch_brand_style_images reads. Behaviour & dark styling unchanged.
// Image pipeline itself is NOT touched.
const BrandMediaManager = ({ getAuthHeader }) => {
  const { lang } = useLanguage();
  const [brandMedia, setBrandMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/brand/media`, { headers: getAuthHeader() })
      .then((r) => { if (alive) setBrandMedia(r.data.media || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [getAuthHeader]);

  const handleUploadMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (brandMedia.length >= 10) { toast.error('Maximum 10 Marken-Medien erlaubt'); e.target.value = ''; return; }
    setUploadingMedia(true);
    try {
      for (const file of files.slice(0, 10 - brandMedia.length)) {
        const formData = new FormData();
        formData.append('file', file);
        const r = await axios.post(`${API}/brand/upload-media`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
        setBrandMedia(prev => [...prev, r.data.media]);
      }
      toast.success('Hochgeladen');
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setUploadingMedia(false); e.target.value = ''; }
  };

  const handleDeleteMedia = async (id) => {
    try {
      await axios.delete(`${API}/brand/media/${id}`, { headers: getAuthHeader() });
      setBrandMedia(prev => prev.filter(m => m.id !== id));
      toast.success('Gelöscht');
    } catch { toast.error('Fehler beim Löschen'); }
  };

  return (
    <Card className="bg-[#1A2B42] border-transparent">
      <CardHeader>
        <CardTitle className="text-white text-base flex items-center gap-2"><Image className="w-4 h-4 text-[#00D9C8]" />{lang === 'de' ? 'Visuelle Referenzbilder' : 'Visual References'}</CardTitle>
        <CardDescription className="text-[#A0AEC0] text-sm">{lang === 'de' ? 'Bilder & Videos als Stil-Referenz für die Bildgenerierung.' : 'Images & videos used as style reference for image generation.'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="block" data-testid="brand-media-upload-label">
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" onChange={handleUploadMedia} className="hidden" data-testid="brand-media-file-input" multiple />
          <div className="flex items-center justify-center gap-2 px-4 py-3 bg-[#0A1628] border border-dashed border-[#1A2B42] rounded-lg cursor-pointer hover:border-[#00D9C8]/50 transition-colors">
            {uploadingMedia ? <Loader2 className="w-5 h-5 text-[#00D9C8] animate-spin" /> : <Upload className="w-5 h-5 text-[#00D9C8]" />}
            <span className="text-[#A0AEC0] text-sm">{uploadingMedia ? (lang === 'de' ? 'Wird hochgeladen...' : 'Uploading...') : (lang === 'de' ? 'Dateien hochladen' : 'Upload files')}</span>
          </div>
        </label>
        <p className="text-[#5A6C7D] text-xs text-center">{brandMedia.length}/10 {lang === 'de' ? 'Dateien' : 'files'}</p>
        {brandMedia.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {brandMedia.map((media) => (
              <div key={media.id} className="relative group rounded-lg overflow-hidden bg-[#0A1628]">
                {media.media_type === 'video' ? (
                  <div className="w-full aspect-square flex items-center justify-center"><Video className="w-8 h-8 text-[#5A6C7D]" /></div>
                ) : (
                  <img src={media.file_url} alt={media.filename} className="w-full aspect-square object-cover" />
                )}
                <button onClick={() => handleDeleteMedia(media.id)} className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BrandMediaManager;
